'use client';

import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import type { CommandDestination } from '../../types/command-palette';
import { CommandPalette } from './CommandPalette';

interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

/**
 * Opens/closes the palette from anywhere inside the workspace subtree — e.g. a sidebar button
 * or an empty-state prompt — without those callers having to own the dialog.
 *
 * Throws outside the provider rather than silently no-opping, since a button that looks
 * clickable but does nothing is the harder bug to spot.
 */
export function useCommandPalette() {
  const context = useContext(CommandPaletteContext);
  if (!context) {
    throw new Error('useCommandPalette must be used within a CommandPaletteProvider');
  }
  return context;
}

export interface CommandPaletteProviderProps {
  destinations: CommandDestination[];
  children: ReactNode;
}

/**
 * Owns the palette's open state, binds Ctrl/Cmd+K, and renders the dialog. Mounted once inside
 * WorkspaceLayout so both portals get it from the shared shell.
 *
 * Note Ctrl/Cmd+B is already taken by the sidebar toggle (`shared/src/ui/sidebar.tsx`); K is
 * free across both apps.
 */
export function CommandPaletteProvider({ destinations, children }: CommandPaletteProviderProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // metaKey covers macOS, ctrlKey covers Windows/Linux. `event.key` is compared
      // case-insensitively because Cmd+Shift+K would otherwise report 'K'.
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;

      // Let the browser keep Cmd+Alt+K and friends (devtools, extensions).
      if (event.altKey) return;

      event.preventDefault();
      setOpen((prev) => !prev);
    };

    // Capture phase so the shortcut still works while focus is inside an input or the Tiptap
    // comment editor, both of which stop propagation on some keydowns.
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const value = useMemo(() => ({ open, setOpen, toggle: () => setOpen((prev) => !prev) }), [open]);

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPalette open={open} onOpenChange={setOpen} destinations={destinations} />
    </CommandPaletteContext.Provider>
  );
}
